/**
 * The inventory screen. Opened with Tab, closed the same way.
 *
 * It is driveable entirely from the keyboard because it can be opened while the
 * pointer is still locked to the canvas — the UI never releases the lock itself,
 * so mouse clicks may not reach these buttons.
 */

import { TUNING } from '../core/config';
import type { ItemKind } from '../core/types';
import { add, button, el, icon } from './dom';
import { ITEM_UI, type SlotView } from './items';
import type { Dispatch, Screen } from './screen';

interface Cell {
  el: HTMLElement;
  glyph: HTMLElement;
  count: HTMLElement;
  kind: ItemKind | null;
  n: number;
}

export class InventoryScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-inventory');

  private readonly cells: Cell[] = [];
  private readonly detailName = el('h3', 'ui-detail-name');
  private readonly detailCount = el('div', 'ui-detail-count');
  private readonly detailDesc = el('p', 'ui-detail-desc');
  private readonly detailGlyph = el('div', 'ui-detail-glyph');
  private readonly useBtn: HTMLButtonElement;
  private selected = 0;

  constructor(private readonly dispatch: Dispatch) {
    const head = el('header', 'ui-head');
    add(head, el('h2', 'ui-head-title', 'INVENTORY'), el('span', 'ui-head-note', 'CARRIED'));

    const grid = el('div', 'ui-slots');
    for (let i = 0; i < TUNING.survival.inventorySlots; i++) {
      const cell = el('button', 'ui-slot empty');
      cell.type = 'button';
      const glyph = el('span', 'ui-slot-glyph');
      const count = el('span', 'ui-slot-count');
      add(cell, el('span', 'ui-slot-key', String(i + 1)), glyph, count);
      // Click to select, click the selected slot again to use it — which also
      // makes Enter on a focused slot do the obvious thing.
      cell.addEventListener('click', () => {
        if (this.selected === i) this.use();
        else this.select(i);
      });
      grid.appendChild(cell);
      this.cells.push({ el: cell, glyph, count, kind: null, n: 0 });
    }

    this.useBtn = button('USE', () => this.use(), 'ui-btn ui-btn-wide');
    const detail = el('div', 'ui-detail');
    add(
      detail,
      this.detailGlyph,
      this.detailName,
      this.detailCount,
      this.detailDesc,
      this.useBtn,
    );

    const body = el('div', 'ui-inventory-body');
    add(body, grid, detail);

    const hint = el('footer', 'ui-hint');
    hint.innerHTML =
      '<span><kbd>&#8592;&#8594;</kbd> select</span><span><kbd>ENTER</kbd> use</span>' +
      '<span><kbd>1</kbd>&ndash;<kbd>4</kbd> quick use</span><span><kbd>TAB</kbd> close</span>';

    add(this.el, head, body, hint);
    this.refreshDetail();
  }

  enter(): void {
    this.refreshDetail();
  }

  key(e: KeyboardEvent): boolean {
    switch (e.code) {
      case 'ArrowRight':
        this.select(this.selected + 1);
        return true;
      case 'ArrowLeft':
        this.select(this.selected - 1);
        return true;
      case 'ArrowDown':
        this.select(this.selected + 4);
        return true;
      case 'ArrowUp':
        this.select(this.selected - 4);
        return true;
      case 'Enter':
      case 'KeyE':
        this.use();
        return true;
      default:
        return false;
    }
  }

  /** `slots` is a shared per-frame snapshot; copy anything worth keeping. */
  setSlots(slots: readonly SlotView[]): void {
    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i]!;
      const view = slots[i];
      const kind = view?.kind ?? null;
      const count = view?.count ?? 0;
      if (kind !== cell.kind) {
        cell.kind = kind;
        cell.el.classList.toggle('empty', kind === null);
        if (kind) {
          const def = ITEM_UI[kind];
          cell.glyph.replaceChildren(icon(def.glyph, 'ui-icon ui-icon-lg'));
          cell.glyph.style.color = def.tint;
          cell.el.setAttribute('aria-label', def.label);
        } else {
          cell.glyph.replaceChildren();
          cell.el.setAttribute('aria-label', `Slot ${i + 1}, empty`);
        }
      }
      if (count !== cell.n) {
        cell.n = count;
        cell.count.textContent = count > 1 ? String(count) : '';
      }
    }
    this.refreshDetail();
  }

  private select(index: number): void {
    const n = this.cells.length;
    const next = ((index % n) + n) % n;
    if (next === this.selected) return;
    this.cells[this.selected]?.el.classList.remove('sel');
    this.selected = next;
    this.refreshDetail();
  }

  private use(): void {
    const cell = this.cells[this.selected];
    if (!cell?.kind || !ITEM_UI[cell.kind].usable) return;
    this.dispatch({ type: 'use-slot', index: this.selected });
  }

  private refreshDetail(): void {
    for (let i = 0; i < this.cells.length; i++) {
      this.cells[i]!.el.classList.toggle('sel', i === this.selected);
    }
    const cell = this.cells[this.selected]!;
    if (!cell.kind) {
      this.detailGlyph.replaceChildren();
      this.detailName.textContent = 'EMPTY';
      this.detailCount.textContent = '';
      this.detailDesc.textContent = 'Nothing in this pocket.';
      this.useBtn.disabled = true;
      this.el.classList.add('nothing');
      return;
    }
    const def = ITEM_UI[cell.kind];
    this.detailGlyph.replaceChildren(icon(def.glyph, 'ui-icon ui-icon-xl'));
    this.detailGlyph.style.color = def.tint;
    this.detailName.textContent = def.label.toUpperCase();
    this.detailCount.textContent = cell.n > 1 ? `${cell.n} carried` : '1 carried';
    this.detailDesc.textContent = def.description;
    this.useBtn.disabled = !def.usable;
    this.el.classList.remove('nothing');
  }
}
